import Box from '@amzn/awsui-components-core/box';
import React from 'react';
import { useAppNavigation } from 'src/components/generic-components/AppNameWithLink';
import { sanitizeHtml } from 'src/utilities/SanitizeHtml';

interface ActionCardProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  /** External URL — renders as <a> with target="_blank" for right-click support */
  href?: string;
  /** Optional preview image/element rendered above the title */
  preview?: React.ReactNode;
  /** If true, description contains HTML and should be sanitized */
  htmlDescription?: boolean;
  /** App navigation props — when appId is provided, card navigates like AppNameWithLink */
  appId?: string;
  embeddingEnabled?: boolean;
  applicationLink?: string;
  urlName?: string | null;
}

/**
 * @feature landing-page
 * @owner das-finsuite-application-dev
 * @description Reusable card component for Quick Actions and Recents/Featured sections.
 * Renders an optional preview area, a title, and a short description.
 * When href is provided, renders as an anchor tag for native right-click "Open in new tab".
 * When appId is provided, uses the same navigation logic as AppNameWithLink.
 */
const ActionCard: React.FC<ActionCardProps> = ({
  title,
  description,
  icon,
  onClick,
  href,
  preview,
  htmlDescription,
  appId,
  embeddingEnabled,
  applicationLink,
  urlName
}) => {
  const appNav = useAppNavigation(appId ?? '', embeddingEnabled, applicationLink, urlName);

  // App navigation takes precedence when appId is provided
  const resolvedHref = appId ? appNav.href : href;
  const resolvedOnClick = appId ? appNav.handleClick : onClick;
  const isExternal = appId ? appNav.isExternal : !!href;

  const handleKeyDown = resolvedOnClick
    ? (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          resolvedOnClick();
        }
      }
    : undefined;

  const Tag = resolvedHref ? 'a' : 'div';
  const linkProps = resolvedHref
    ? isExternal
      ? { href: resolvedHref, target: '_blank' as const, rel: 'noopener noreferrer' }
      : { href: resolvedHref }
    : {};
  const interactionProps = !resolvedHref
    ? {
        onClick: resolvedOnClick,
        onKeyDown: handleKeyDown,
        role: resolvedOnClick ? ('button' as const) : undefined,
        tabIndex: resolvedOnClick ? 0 : undefined
      }
    : {
        ...(resolvedOnClick && {
          onClick: (e: React.MouseEvent) => {
            e.preventDefault();
            resolvedOnClick();
          },
          onKeyDown: handleKeyDown
        })
      };

  return (
    <Tag className="landing-action-card" {...linkProps} {...interactionProps}>
      {preview && <div className="landing-action-card__preview">{preview}</div>}
      <div className="landing-action-card__body">
        {icon && <div className="landing-action-card__icon">{icon}</div>}
        <div className="landing-action-card__text">
          <Box variant="h4" fontSize="heading-s" color="text-body-secondary">
            {title}
          </Box>
          {htmlDescription ? (
            <Box variant="p" fontSize="body-s" color="text-body-secondary" margin={{ top: 'xxs' }}>
              {/* Safe: HTML is sanitized via DOMPurify in src/utilities/SanitizeHtml.ts before rendering */}
              <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }} />
            </Box>
          ) : (
            <Box variant="p" fontSize="body-s" color="text-body-secondary" margin={{ top: 'xxs' }}>
              {description}
            </Box>
          )}
        </div>
      </div>
    </Tag>
  );
};

export default ActionCard;
